/* Luova.fi shell behaviour: announcement rotation, desktop menu hover intent, mobile menu
   panel. Cart drawer, predictive search and accounts stay on Dawn's own scripts. */
(() => {
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Announcement bar: one message at a time, 6 s beat, paused on hover and focus. --- */
  function initAnnouncement(root) {
    const messages = [...root.querySelectorAll('.luova-announcement__message')];
    if (messages.length < 2 || reducedMotion()) return;
    let i = 0;
    let paused = false;
    root.addEventListener('mouseenter', () => (paused = true));
    root.addEventListener('mouseleave', () => (paused = false));
    root.addEventListener('focusin', () => (paused = true));
    root.addEventListener('focusout', () => (paused = false));
    setInterval(() => {
      if (paused) return;
      messages[i].hidden = true;
      i = (i + 1) % messages.length;
      messages[i].hidden = false;
    }, 6000);
  }

  /* --- Desktop menu -----------------------------------------------------------
     Hover intent: the first hover opens at once; switching to another item waits until
     the pointer rests on it (180 ms), so travelling down into an open panel across other
     items never swaps the panel. Keyboard: focusing an item opens its panel, Escape
     closes, leaving the menu closes. */
  function initMenu(nav) {
    const items = [...nav.querySelectorAll('[data-luova-menu-item]')];
    let current = null;
    let timer = null;

    const panelOf = (item) => item.querySelector('[data-luova-menu-panel-desktop]');
    const linkOf = (item) => item.querySelector('.luova-menu__link');

    const open = (item) => {
      clearTimeout(timer);
      if (current === item) return;
      if (current) close(current);
      const panel = item && panelOf(item);
      if (!panel) {
        current = null;
        return;
      }
      panel.hidden = false;
      item.classList.add('is-open');
      linkOf(item).setAttribute('aria-expanded', 'true');
      current = item;
    };

    const close = (item) => {
      const panel = panelOf(item);
      if (panel) panel.hidden = true;
      item.classList.remove('is-open');
      const link = linkOf(item);
      if (link.hasAttribute('aria-expanded')) link.setAttribute('aria-expanded', 'false');
      if (current === item) current = null;
    };

    const closeAll = () => {
      clearTimeout(timer);
      if (current) close(current);
    };

    items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        clearTimeout(timer);
        if (!current) {
          open(item);
          return;
        }
        if (current === item) return;
        timer = setTimeout(() => open(item), 180);
      });
      linkOf(item).addEventListener('focus', () => open(item));
      const panel = panelOf(item);
      if (panel) panel.addEventListener('mouseenter', () => clearTimeout(timer));
    });

    /* Fit on one row: spec type → compact type → the last departments move into "Lisää"
       (its panel shows a column for each department moved out). Nothing is dropped. */
    const list = nav.querySelector('.luova-menu__list');
    const bar = nav.querySelector('.luova-menu__bar');
    const more = nav.querySelector('[data-luova-menu-more]');
    const depts = items.filter((item) => item !== more);
    const overflows = () => list.scrollWidth > bar.clientWidth - parseFloat(getComputedStyle(bar).paddingLeft) * 2 + 1;
    const fit = () => {
      if (!bar.clientWidth) return;
      closeAll();
      nav.classList.remove('is-compact');
      depts.forEach((item) => (item.hidden = false));
      if (more) more.hidden = true;
      if (!overflows()) return;
      nav.classList.add('is-compact');
      if (!overflows() || !more) return;

      more.hidden = false;
      const cols = more.querySelectorAll('[data-luova-more-col]');
      cols.forEach((col) => (col.hidden = true));
      for (let i = depts.length - 1; i > 0 && overflows(); i -= 1) {
        depts[i].hidden = true;
        const col = more.querySelector(`[data-luova-more-col="${depts[i].dataset.luovaMenuDept}"]`);
        if (col) col.hidden = false;
      }
      const moreLink = linkOf(more);
      moreLink.classList.toggle('is-current', depts.some((item) => item.hidden && linkOf(item).classList.contains('is-current')));
    };
    fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    if ('ResizeObserver' in window) new ResizeObserver(fit).observe(bar);
    else window.addEventListener('resize', fit);

    nav.addEventListener('mouseleave', closeAll);
    nav.addEventListener('focusout', (e) => {
      if (!nav.contains(e.relatedTarget)) closeAll();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape' || !current) return;
      const link = linkOf(current);
      closeAll();
      if (nav.contains(document.activeElement)) link.focus();
    });
  }

  /* --- Mobile menu panel: slides in from the left, one level at a time. --- */
  function initMobileMenu(panel) {
    const sheet = panel.querySelector('.luova-drawer-menu__sheet');
    const openers = document.querySelectorAll('[data-luova-menu-open]');
    let opener = null;

    const showLevel = (key) => {
      panel.querySelectorAll('[data-luova-menu-level]').forEach((level) => {
        level.hidden = level.dataset.luovaMenuLevel !== key;
      });
      panel.querySelectorAll('[data-luova-menu-open-level]').forEach((btn) => {
        btn.setAttribute('aria-expanded', String(btn.dataset.luovaMenuOpenLevel === key));
      });
      sheet.scrollTop = 0;
    };

    const open = (trigger) => {
      opener = trigger;
      showLevel('root');
      panel.hidden = false;
      void panel.offsetWidth; /* commit the closed state so the slide-in transition runs */
      panel.classList.add('is-open');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('overflow-hidden');
      if (typeof trapFocus === 'function') trapFocus(sheet, sheet.querySelector('button, a'));
    };

    const close = () => {
      panel.classList.remove('is-open');
      openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('overflow-hidden');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(opener);
      else if (opener) opener.focus();
      const hide = () => (panel.hidden = true);
      if (reducedMotion()) hide();
      else {
        const onEnd = (e) => {
          if (e.target !== sheet) return;
          sheet.removeEventListener('transitionend', onEnd);
          hide();
        };
        sheet.addEventListener('transitionend', onEnd);
      }
    };

    openers.forEach((b) => b.addEventListener('click', () => open(b)));
    panel.querySelectorAll('[data-luova-menu-close]').forEach((b) => b.addEventListener('click', close));
    panel.querySelectorAll('[data-luova-menu-open-level]').forEach((b) =>
      b.addEventListener('click', () => {
        showLevel(b.dataset.luovaMenuOpenLevel);
        const first = panel.querySelector(`[data-luova-menu-level="${b.dataset.luovaMenuOpenLevel}"] button, [data-luova-menu-level="${b.dataset.luovaMenuOpenLevel}"] a`);
        if (first) first.focus();
      })
    );
    panel.querySelectorAll('[data-luova-menu-back]').forEach((b) =>
      b.addEventListener('click', () => {
        const from = b.closest('[data-luova-menu-level]').dataset.luovaMenuLevel;
        showLevel('root');
        const btn = panel.querySelector(`[data-luova-menu-open-level="${from}"]`);
        if (btn) btn.focus();
      })
    );
    panel.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
    });
    /* Leaving the mobile layout with the panel open (rotating a tablet) closes it. */
    window.matchMedia('(min-width: 990px)').addEventListener('change', (e) => {
      if (e.matches && !panel.hidden) close();
    });
  }

  /* --- Rich-text tables: own sideways scroll, never the page's. ------------------------
     Every table in a product description, page or policy body is wrapped in
     .luova-table-scroll. The wrapper shades the side that has more table behind it and
     becomes focusable (keyboard scrolling) only while the table is wider than the column.
     Short cells with a digit in them (sizes, prices, codes) are kept on one line. */
  const RICH_TEXT = '.luova-prose, .rte, .shopify-policy__body';
  function wrapTables(root) {
    root.querySelectorAll(`:is(${RICH_TEXT}) table`).forEach((table) => {
      if (table.closest('.luova-table-scroll')) return;
      const outer = document.createElement('div');
      outer.className = 'luova-table-scroll';
      const inner = document.createElement('div');
      inner.className = 'luova-table-scroll__inner';
      table.parentNode.insertBefore(outer, table);
      outer.appendChild(inner);
      inner.appendChild(table);

      table.querySelectorAll('td, th').forEach((cell) => {
        const text = cell.textContent.trim();
        if (text.length <= 20 && /\d/.test(text)) cell.classList.add('is-num');
      });

      const update = () => {
        const max = inner.scrollWidth - inner.clientWidth;
        const scrollable = max > 1;
        outer.classList.toggle('has-more-left', scrollable && inner.scrollLeft > 1);
        outer.classList.toggle('has-more-right', scrollable && inner.scrollLeft < max - 1);
        if (scrollable) inner.setAttribute('tabindex', '0');
        else inner.removeAttribute('tabindex');
      };
      inner.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(inner);
      else window.addEventListener('resize', update);
      update();
    });
  }

  /* Each element is wired once, so a theme-editor section reload does not double-bind. */
  const once = (fn) => (el) => {
    if (el.dataset.luovaReady) return;
    el.dataset.luovaReady = 'true';
    fn(el);
  };

  function init() {
    document.querySelectorAll('[data-luova-announcement]').forEach(once(initAnnouncement));
    document.querySelectorAll('[data-luova-menu]').forEach(once(initMenu));
    document.querySelectorAll('[data-luova-menu-panel]').forEach(once(initMobileMenu));
    wrapTables(document);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* Theme editor: re-run when a shell section is re-rendered. */
  document.addEventListener('shopify:section:load', init);
})();
